import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { articleImage } from "@/mocks/categories";

const valueIcons = [
  "ri-stack-line",
  "ri-search-line",
  "ri-shield-check-line",
  "ri-truck-line",
  "ri-scales-3-line",
  "ri-lock-line",
];

const faqKeys = ["q1", "q2", "q3", "q4"];

export default function InfoSections() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const valueCards = ["c1", "c2", "c3", "c4", "c5", "c6"];

  return (
    <>
      {/* About */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
            <div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{t("about.title")}</h2>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">{t("about.p1")}</p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">{t("about.p2")}</p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
              >
                {t("about.cta")}
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["ri-grid-fill", "ri-file-list-3-line", "ri-database-2-line", "ri-flashlight-line"].map((icon, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${icon} text-xl`}></i>
                  </span>
                  <p className="mt-4 text-xs md:text-sm font-medium text-foreground-700">
                    {t(`value.${valueCards[i]}.title`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{t("value.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-foreground-600">{t("value.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {valueCards.map((key, i) => (
              <div
                key={key}
                className="rounded-lg border border-background-200 bg-background-50 p-6 transition-all hover:-translate-y-0.5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${valueIcons[i]} text-2xl`}></i>
                </span>
                <h3 className="mt-5 font-heading text-base md:text-lg font-semibold text-foreground-900">
                  {t(`value.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(`value.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Article promo */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="h-60 md:h-80 w-full">
                <img
                  src={articleImage}
                  alt="USFans Spreadsheet beginner guide to product research"
                  title="USFans Spreadsheet guide"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="flex flex-col justify-center p-6 md:p-10">
                <span className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
                  <i className="ri-book-open-line text-sm"></i>
                  {t("article.tag")}
                </span>
                <h2 className="mt-4 font-heading text-xl md:text-3xl font-bold text-foreground-950">
                  {t("article.title")}
                </h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">{t("article.subtitle")}</p>
                <div className="mt-6">
                  <Link
                    to="/article"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {t("article.button")}
                    <i className="ri-arrow-right-line text-base"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{t("faq.title")}</h2>
          </div>
          <div className="mt-8 space-y-3">
            {faqKeys.map((key, i) => (
              <div key={key} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900">{t(`faq.${key}`)}</span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                    <i className={`text-base transition-transform ${openFaq === i ? "rotate-45" : ""} ri-add-line`}></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm leading-relaxed text-foreground-600">{t(`faq.a${i + 1}`)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}